import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import DateTimeField from "components/Common/DateTimeField"
import PropTypes from "prop-types"
import { Container } from "reactstrap"
import {
  fetchAnalyticsAccess,
  fetchAnalyticsView,
} from "./api"
import ExportCurrentView from "./ExportCurrentView"
import ParticipationView from "./ParticipationView"
import OverviewView from "./OverviewView"
import RevenueView from "./RevenueView"
import RegionsClubsView from "./RegionsClubsView"
import DiagnosticsView from "./DiagnosticsView"
import ReviewsView from "./ReviewsView"
import AnalyticsFilterFields from "./AnalyticsFilterFields"
import { QuerySummary } from "./DashboardComponents"
import { DefinitionExperience, DictionaryButton } from "./DefinitionExperience"
import { formatTimestamp } from "./formatters"
import {
  DEFAULT_QUERY,
  TABS,
  changeQuery,
  querySearch,
  readQuery,
  validCustomRange,
} from "./queryState"

const viewLabel = view => TABS.find(([key]) => key === view)?.[1] || "This view"

const requestMessage = (requestError, query) => {
  const payload = requestError?.response?.data
  const recoverable = Boolean(payload?.data?.recoverable)
  if (recoverable) {
    return {
      recoverable,
      message: `${viewLabel(payload?.data?.scope || query.view)} is temporarily unavailable. Your other exact-period snapshots remain available.`,
    }
  }
  return {
    recoverable: false,
    message: payload?.message || "Analytics could not be loaded.",
  }
}

const LoadingState = () => (
  <div className="analytics-state analytics-loading-state" role="status" aria-label="Analytics are loading">
    <div className="analytics-skeleton" aria-hidden="true">
      <i /><i /><i />
    </div>
    <span className="analytics-loader" />
    <span>Loading analytics…</span>
  </div>
)

const AnalyticsWorkspace = ({ history, location, onAccessRequired }) => {
  const snapshotsRef = useRef(new Map())
  const bypassNextRef = useRef(false)
  const [snapshotState, setSnapshotState] = useState({ key: null, data: null })
  const [requestState, setRequestState] = useState("loading")
  const [error, setError] = useState(null)
  const [announcement, setAnnouncement] = useState("")
  const [refreshKey, setRefreshKey] = useState(0)
  const [participationBreakdownKey, setParticipationBreakdownKey] = useState(null)
  const canonicalSearch = querySearch(readQuery(location.search))
  const query = useMemo(() => readQuery(canonicalSearch), [canonicalSearch])
  const [customRangeDraft, setCustomRangeDraft] = useState(() => ({
    startDate: query.startDate || "",
    endDate: query.endDate || "",
  }))
  const cachedSnapshot = snapshotsRef.current.get(canonicalSearch)
  const snapshot = snapshotState.key === canonicalSearch ? snapshotState.data : cachedSnapshot || null
  const initialLoading = requestState === "loading" && !snapshot
  const refreshing = requestState === "refreshing" && Boolean(snapshot)
  const participationBreakdownKeys = snapshot?.view === "participation"
    ? Object.keys(snapshot.breakdowns || {})
    : []
  const visibleParticipationBreakdownKey = participationBreakdownKeys.includes(participationBreakdownKey)
    ? participationBreakdownKey
    : participationBreakdownKeys[0]
  const visibleCsvTableKeys = snapshot?.view === "participation" && visibleParticipationBreakdownKey
    ? [`breakdown:${visibleParticipationBreakdownKey}`]
    : null

  useEffect(() => {
    if (query.period !== "custom") return
    setCustomRangeDraft({
      startDate: query.startDate || "",
      endDate: query.endDate || "",
    })
  }, [query.endDate, query.period, query.startDate])

  useEffect(() => {
    if (location.search !== canonicalSearch) {
      history.replace({ pathname: location.pathname, search: canonicalSearch })
    }
  }, [canonicalSearch, history, location.pathname, location.search])

  useEffect(() => {
    let active = true
    const cached = snapshotsRef.current.get(canonicalSearch) || null
    const bypassCache = bypassNextRef.current
    bypassNextRef.current = false
    setSnapshotState({ key: canonicalSearch, data: cached })
    setRequestState(cached ? "refreshing" : "loading")
    setError(null)
    setAnnouncement(cached ? "Refreshing analytics while the last successful snapshot remains visible." : "")
    fetchAnalyticsView(bypassCache ? { ...query, refresh: "true" } : query)
      .then(response => {
        if (!active) return
        const data = response.data
        snapshotsRef.current.set(canonicalSearch, data)
        setSnapshotState({ key: canonicalSearch, data })
        setRequestState("idle")
        setAnnouncement(`${bypassCache ? "Analytics refreshed" : "Analytics loaded"}. Snapshot ${data.metadata?.snapshotId || "unknown"} is ready.`)
      })
      .catch(requestError => {
        if (!active) return
        const message = requestError?.response?.data?.message
        if ([401, 403].includes(requestError?.response?.status)) {
          onAccessRequired(message)
          return
        }
        setError(requestMessage(requestError, query))
        setRequestState("error")
        setSnapshotState({ key: canonicalSearch, data: cached })
        setAnnouncement("Analytics request failed. The last successful exact-period snapshot is preserved when available.")
      })
    return () => { active = false }
  }, [canonicalSearch, onAccessRequired, query, refreshKey])

  const refresh = () => {
    bypassNextRef.current = true
    setRefreshKey(value => value + 1)
  }

  const updateQuery = changes => {
    history.push({
      pathname: location.pathname,
      search: querySearch(changeQuery(query, changes, snapshot?.ranges?.current)),
    })
  }

  const customRangeDirty = customRangeDraft.startDate !== query.startDate
    || customRangeDraft.endDate !== query.endDate
  const customRangeIsValid = validCustomRange(customRangeDraft.startDate, customRangeDraft.endDate)
  const applyCustomRange = () => {
    if (!customRangeIsValid) return
    updateQuery(customRangeDraft)
  }

  const clearFilters = () => {
    history.push({
      pathname: location.pathname,
      search: querySearch({ ...DEFAULT_QUERY, view: query.view }),
    })
  }

  const qualityStates = [
    ...(snapshot?.kpis || []),
    ...(snapshot?.diagnostics?.indicators || []),
  ].map(metric => metric.current?.quality?.state)
  const qualityNotice = qualityStates.includes("unavailable")
    ? "Some values are unavailable because objective evidence is missing; they are not shown as zero."
    : qualityStates.includes("partial")
      ? "Some values have partial source coverage. Open their definition or Diagnostics for the exact reason."
      : qualityStates.includes("estimated")
        ? "Some values use a declared reproducible estimate. Open their definition for the exact basis."
        : ""

  const renderSnapshot = () => {
    if (!snapshot) return null
    if (snapshot.view === "reviews") return <ReviewsView snapshot={snapshot} />
    if (snapshot.view === "participation") {
      return (
        <ParticipationView
          snapshot={snapshot}
          selectedBreakdownKey={visibleParticipationBreakdownKey}
          onSelectedBreakdownChange={setParticipationBreakdownKey}
        />
      )
    }
    if (snapshot.view === "revenue") return <RevenueView snapshot={snapshot} />
    if (snapshot.view === "regions-clubs") {
      return <RegionsClubsView snapshot={snapshot} onLensChange={lens => updateQuery({ lens })} />
    }
    if (snapshot.view === "diagnostics") return <DiagnosticsView snapshot={snapshot} />
    return <OverviewView snapshot={snapshot} onNavigate={updateQuery} />
  }

  return (
    <div className="page-content extended-analytics-page">
      <DefinitionExperience snapshot={snapshot}>
        <Container fluid>
          <header className="analytics-page-header">
            <div>
              <p className="analytics-eyebrow">Management dashboard</p>
              <h1>Extended Analytics</h1>
              <p>One comparable view of participation, revenue, player health and regional demand versus active supply.</p>
            </div>
            <div className="analytics-metadata" aria-label="Analytics metadata">
              <span>Contract <strong>v{snapshot?.contractVersion || "1.0"}</strong></span>
              {snapshot && <span>Data through <strong>{snapshot.metadata.dataAvailableThrough}</strong></span>}
              {snapshot && <span>Generated <strong>{formatTimestamp(snapshot.metadata.generatedAt)}</strong></span>}
              {snapshot && <span>Calculation <strong>{snapshot.metadata.calculationVersion}</strong></span>}
              {snapshot && <span>Definitions <strong>{snapshot.metadata.definitionVersion}</strong></span>}
              {snapshot && <span>Snapshot <strong>{snapshot.metadata.snapshotId}</strong></span>}
              <DictionaryButton />
              <ExportCurrentView
                snapshot={snapshot}
                query={query}
                visibleCsvTableKeys={visibleCsvTableKeys}
                onAccessRequired={onAccessRequired}
              />
              <button type="button" onClick={refresh} disabled={initialLoading || refreshing}>
                {refreshing ? "Refreshing…" : "Refresh"}
              </button>
            </div>
          </header>

          <section className="analytics-control-panel" aria-label="Analytics filters">
            <div className="analytics-filter-grid">
              <AnalyticsFilterFields query={query} snapshot={snapshot} onChange={updateQuery} />
            </div>
            {query.period === "custom" && (
              <div className="analytics-custom-range">
                <label><span>From</span><DateTimeField mode="date" value={customRangeDraft.startDate} rangeEnd={customRangeDraft.endDate || ""} onChange={event => {
                  const startDate = event.target.value
                  setCustomRangeDraft(current => ({ ...current, startDate }))
                }} /></label>
                <label><span>To</span><DateTimeField mode="date" value={customRangeDraft.endDate} rangeStart={customRangeDraft.startDate || ""} onChange={event => {
                  const endDate = event.target.value
                  setCustomRangeDraft(current => ({ ...current, endDate }))
                }} /></label>
                <button type="button" onClick={applyCustomRange} disabled={!customRangeIsValid || !customRangeDirty}>
                  Apply range
                </button>
              </div>
            )}
            {snapshot && <QuerySummary snapshot={snapshot} />}
            <p className="analytics-filter-note">
              Activity type is explained locally only where it clarifies a KPI. Activity Location is global; Player Home is a demand-only lens inside Regions & Clubs.
            </p>
          </section>

          <nav className="analytics-tabs" role="tablist" aria-label="Analytics perspectives">
            {TABS.map(([tabValue, label]) => (
              <button
                type="button"
                role="tab"
                aria-selected={query.view === tabValue}
                className={query.view === tabValue ? "is-active" : ""}
                onClick={() => updateQuery({ view: tabValue })}
                key={tabValue}
              >
                {label}
              </button>
            ))}
          </nav>

          <div className="sr-only" role="status" aria-live="polite">{announcement}</div>
          {refreshing && (
            <div className="analytics-refresh-state" role="status">
              <span className="analytics-loader" aria-hidden="true" />
              <span>Refreshing analytics…</span>
            </div>
          )}
          {error && snapshot && (
            <div className="analytics-inline-error" role="alert">
              <div><strong>{error.message}</strong><span>The values below are the last successful snapshot for this exact selection.</span></div>
              <button type="button" onClick={refresh}>Try again</button>
            </div>
          )}
          {snapshot && !snapshot.metadata?.empty && qualityNotice && (
            <div className="analytics-quality-banner" role="note">{qualityNotice}</div>
          )}

          <main className="analytics-content" aria-busy={initialLoading || refreshing}>
            {initialLoading && <LoadingState />}
            {error && !snapshot && (
              <div className="analytics-state is-error" role="alert">
                <strong>We could not load this view.</strong>
                <span>{error.message}</span>
                <button type="button" onClick={refresh}>Try again</button>
              </div>
            )}
            {!initialLoading && snapshot?.metadata?.empty && (
              <div className="analytics-state analytics-empty-state" role="status" aria-label="Empty analytics result">
                <strong>No eligible activity in this selection.</strong>
                <span>This is a valid empty result, not a request failure. Try a broader period or Activity Location.</span>
                <button type="button" onClick={clearFilters}>Clear filters</button>
              </div>
            )}
            {!initialLoading && snapshot && !snapshot.metadata?.empty && renderSnapshot()}
          </main>
        </Container>
      </DefinitionExperience>
    </div>
  )
}

AnalyticsWorkspace.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
  }).isRequired,
  onAccessRequired: PropTypes.func.isRequired,
}

const ExtendedAnalytics = props => {
  const [accessState, setAccessState] = useState("checking")
  const [accessError, setAccessError] = useState("")
  const [retry, setRetry] = useState(0)
  useEffect(() => {
    let active = true
    setAccessState("checking")
    fetchAnalyticsAccess()
      .then(response => {
        if (!active) return
        setAccessState(response.data?.unlocked === true ? "unlocked" : "denied")
        setAccessError("Only Super admins can access Extended Analytics.")
      })
      .catch(error => {
        if (!active) return
        setAccessState("denied")
        setAccessError(error?.response?.status === 403
          ? "Only Super admins can access Extended Analytics."
          : "Analytics access could not be verified. Please try again.")
      })
    return () => { active = false }
  }, [retry])
  const requireAccess = useCallback(() => {
    setAccessState("denied")
    setAccessError("Your access could not be verified. Please try again.")
  }, [])
  if (accessState === "unlocked") {
    return <AnalyticsWorkspace {...props} onAccessRequired={requireAccess} />
  }
  return <div className="page-content extended-analytics-page analytics-access-page"><Container fluid>
    {accessState === "checking"
      ? <main className="analytics-content" aria-busy="true"><LoadingState /></main>
      : <section className="analytics-access-card" role="alert">
        <div><h1>Extended Analytics access</h1><p>{accessError}</p>
          <button type="button" onClick={() => setRetry(value => value + 1)}>Retry access</button></div>
      </section>}
  </Container></div>
}

ExtendedAnalytics.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
  }).isRequired,
}

export default ExtendedAnalytics
